import React, { useRef, useEffect, useState } from 'react'
import Link from 'next/link'
import widgetRoutes from 'enums/routes'
import styled from '@emotion/styled'
import Icon from 'shared/library/atoms/Icon'
import { useRouter } from 'next/router'
import { defined } from 'shared/presenters/presenter'
import GoogleStructuredDataBreadcrumb from 'library/molecules/GoogleStructuredData/GoogleStructuredDataBreadcrumbs'

export type IPreviousPage = {
  name: string
  link?: string
  onClick?: () => void
  currentPage?: boolean
  image?: string
}

export interface BreadcrumbProps {
  pages: IPreviousPage[]
  firstLinkAsHome?: boolean
  variant?: 'widget' | 'marketplace'
}

/**
 * The Breadcrumb navigation provides links to some of the main previousPage
 * within the navigational hierarchy the user navigated through and shows
 * the user's current location in the widget.
 *
 * The last item doesn't need the href because this is alocated for the current page.
 *
 * This is done manually rather than automatic with next/router because not all
 * pages are present in the breadcrumb and some of them need to display
 * different text.
 */

const Breadcrumb = ({ pages, firstLinkAsHome, variant }: BreadcrumbProps) => {
  const { query } = useRouter()
  const widgetSlug = query.widgetSlug as string
  const breadcrumbs = firstLinkAsHome ? pages.slice(1) : pages
  const variantClass = variant === 'marketplace' ? 'marketplace' : 'widget'

  const containerRef = useRef<HTMLUListElement | null>(null)
  const [isRestrictedScreenSize, setIsRestrictedScreenSize] = useState(true)

  useEffect(() => {
    const checkScreenSize = () => {
      const el = containerRef.current
      if (el) {
        setIsRestrictedScreenSize(el.clientWidth < 780)
      }
    }

    checkScreenSize()

    // Optional: re-check on window resize
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const displayBreadcrumbs = !isRestrictedScreenSize
    ? breadcrumbs
    : breadcrumbs.slice(-2)

  const displayHomeLink = !isRestrictedScreenSize || breadcrumbs.length <= 1

  return (
    <>
      <StyledBreadcrumb variant={variantClass} aria-label="breadcrumb">
        <ul className="breadcrumb" ref={containerRef}>
          {displayHomeLink && (
            <li>
              <Link
                href={
                  firstLinkAsHome
                    ? defined(pages[0].link)
                    : widgetRoutes.HOME.url({ widgetSlug })
                }
                passHref
              >
                <Icon name="house" size={20} />
              </Link>
            </li>
          )}
          {displayBreadcrumbs.map((breadcrumb, index) => (
            <li
              key={breadcrumb.name}
              className="breadcrumb-item"
              // Inform if this is the current page.
              aria-current={
                index === breadcrumbs.length - 1 ? 'page' : undefined
              }
              aria-label={breadcrumb.name}
            >
              {!displayHomeLink && index === 0 ? null : (
                <StyledRightIcon name="arrow-right" size={12} />
              )}
              <span className="breadcrumb-text">
                {breadcrumb.currentPage ? (
                  breadcrumb.name
                ) : breadcrumb.onClick ? (
                  <a onClick={breadcrumb.onClick} className="breadcrumb-link">
                    {breadcrumb.name}
                  </a>
                ) : breadcrumb.link ? (
                  <Link href={breadcrumb.link} className="breadcrumb-link">
                    {breadcrumb.name}
                  </Link>
                ) : (
                  breadcrumb.name
                )}
              </span>
            </li>
          ))}
        </ul>
      </StyledBreadcrumb>
      <GoogleStructuredDataBreadcrumb breadcrumbs={pages} />
    </>
  )
}

export default Breadcrumb

export const StyledBreadcrumb = styled.nav<{
  variant: 'widget' | 'marketplace'
}>(({ theme, variant }) => ({
  ...(variant === 'widget' && {
    borderBottom: `${theme.border}`
  }),
  background: variant === 'widget' ? theme.colors.white : 'transparent',
  width: '100%',
  padding: `${theme.space[0]}px ${theme.space[4]}px`,
  ...theme.fontStyle.p3,
  '.breadcrumb': {
    display: 'flex',
    alignItems: 'center',
    height: 50,
    padding: 0,
    margin: 0,
    listStyle: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    overflowX: 'scroll',
    ...(variant === 'widget'
      ? theme.mixins.centeredContainer(theme.currentMaxWidth)
      : null)
  },
  '.breadcrumb-item': {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',

    '&[aria-current="page"]': {
      cursor: 'auto',
      fontWeight: 500
    }
  },
  '.breadcrumb-text': {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  '.breadcrumb-link': {
    alignItems: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.colors.text.primary
  },
  [theme.mediaQueries.lg]: {
    marginLeft: 0,
    marginRight: 'auto'
  }
}))

const StyledRightIcon = styled(Icon)(({ theme }) => ({
  minWidth: 15,
  marginLeft: theme.space[1],
  marginRight: theme.space[1]
}))
